import React, { useState, useEffect } from 'react';
import { AlertCircle, Plus, Search, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import ProblemCard from '../components/ProblemCard';
import ProblemDetailModal from '../components/ProblemDetailModal';
import ProblemFormModal from '../components/ProblemFormModal';
import { fetchProblems, createProblem, updateProblem, deleteProblem, saveSolutions } from '../utils/supabaseHelpers';

const Dashboard = () => {
  const [problems, setProblems] = useState([]);
  const [filteredProblems, setFilteredProblems] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingProblem, setEditingProblem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    domain: 'all',
    status: 'all',
    priority: 'all'
  });

  useEffect(() => {
    loadProblems();
  }, []);

  const loadProblems = async () => {
    try {
      setLoading(true);
      const data = await fetchProblems();
      setProblems(data);
    } catch (error) {
      toast.error('Failed to load problems: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = problems;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Domain filter
    if (filters.domain !== 'all') {
      filtered = filtered.filter(p => p.domain === filters.domain);
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(p => p.status === filters.status);
    }

    // Priority filter
    if (filters.priority !== 'all') {
      filtered = filtered.filter(p => p.priority === filters.priority);
    }

    setFilteredProblems(filtered);
  }, [problems, searchTerm, filters]);

  const handleCreateProblem = async (problemData) => {
    try {
      const newProblem = await createProblem({
        title: problemData.title,
        description: problemData.description,
        domain: problemData.domain,
        priority: problemData.priority,
        status: problemData.status,
        due_date: problemData.dueDate || null,
        notes: problemData.notes || ''
        // Removed solutions - it's a separate table, not a column
      });
      
      // Save solutions to separate table
      if (problemData.solutions && problemData.solutions.length > 0) {
        await saveSolutions(newProblem.id, problemData.solutions);
      }
      
      setProblems([newProblem, ...problems]);
      toast.success('Problem created successfully');
      setShowFormModal(false);
    } catch (error) {
      toast.error('Failed to create problem: ' + error.message);
    }
  };

  const handleUpdateProblem = async (problemData) => {
    try {
      const updatedProblem = await updateProblem(problemData.id, {
        title: problemData.title,
        description: problemData.description,
        domain: problemData.domain,
        priority: problemData.priority,
        status: problemData.status,
        due_date: problemData.dueDate || null,
        notes: problemData.notes || ''
        // Removed solutions - it's a separate table, not a column
      });
      
      // Save solutions to separate table
      await saveSolutions(problemData.id, problemData.solutions || []);
      
      setProblems(problems.map(p => p.id === updatedProblem.id ? updatedProblem : p));
      toast.success('Problem updated successfully');
      setShowFormModal(false);
      setEditingProblem(null);
      
      if (selectedProblem?.id === updatedProblem.id) {
        setSelectedProblem(updatedProblem);
      }
    } catch (error) {
      toast.error('Failed to update problem: ' + error.message);
    }
  };

  const handleDeleteProblem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this problem?')) return;
    
    try {
      await deleteProblem(id);
      setProblems(problems.filter(p => p.id !== id));
      toast.success('Problem deleted successfully');
      setShowDetailModal(false);
      setSelectedProblem(null);
    } catch (error) {
      toast.error('Failed to delete problem: ' + error.message);
    }
  };

  const handleEditClick = (problem) => {
    setEditingProblem(problem);
    setShowFormModal(true);
    setShowDetailModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading problems...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="dashboard-page">
      {/* Search & Filters */}
      <div className="glass-card p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full glass-input"
              style={{ paddingLeft: '45px' }}
              data-testid="search-input"
            />
          </div>

          <div className="relative">
            <select
              value={filters.domain}
              onChange={(e) => setFilters({ ...filters, domain: e.target.value })}
              className="glass-input cursor-pointer"
              style={{ paddingRight: '40px' }}
              data-testid="filter-domain"
            >
              <option value="all">All Domains</option>
              <option value="mechanical">Mechanical</option>
              <option value="software">Software</option>
              <option value="electronics">Electronics</option>
              <option value="management">Management</option>
            </select>
            <ChevronDown size={16} className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" style={{ right: '12px' }} />
          </div>

          <div className="relative">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="glass-input cursor-pointer"
              style={{ paddingRight: '40px' }}
              data-testid="filter-status"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="under_discussion">Under Discussion</option>
              <option value="solution_decided">Solution Decided</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
            <ChevronDown size={16} className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" style={{ right: '12px' }} />
          </div>

          <div className="relative">
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="glass-input cursor-pointer"
              style={{ paddingRight: '40px' }}
              data-testid="filter-priority"
            >
              <option value="all">All Priority</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <ChevronDown size={16} className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" style={{ right: '12px' }} />
          </div>

          <button
            onClick={() => {
              setEditingProblem(null);
              setShowFormModal(true);
            }}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
            data-testid="create-problem-btn"
          >
            <Plus size={20} />
            Create Problem
          </button>
        </div>
      </div>

      {/* Problem Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
        {filteredProblems.map((problem) => (
          <ProblemCard
            key={problem.id}
            problem={problem}
            onClick={() => {
              setSelectedProblem(problem);
              setShowDetailModal(true);
            }}
          />
        ))}
      </div>

      {filteredProblems.length === 0 && !loading && (
        <div className="glass-card p-12 text-center" data-testid="no-problems">
          <AlertCircle className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-400 text-lg mb-4">
            {problems.length === 0 ? 'No problems yet. Create your first problem!' : 'No problems found matching your criteria'}
          </p>
          {problems.length === 0 && (
            <button
              onClick={() => setShowFormModal(true)}
              className="btn-primary"
            >
              Create Problem
            </button>
          )}
        </div>
      )}

      {filteredProblems.length > 0 && (
        <div className="text-center text-gray-400" data-testid="problem-count">
          Showing {filteredProblems.length} of {problems.length} problems
        </div>
      )}

      {/* Modals */}
      {showDetailModal && selectedProblem && (
        <ProblemDetailModal
          problem={selectedProblem}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedProblem(null);
          }}
          onEdit={() => handleEditClick(selectedProblem)}
          onDelete={() => handleDeleteProblem(selectedProblem.id)}
          onUpdate={async (updatedProblem) => {
            try {
              const updated = await updateProblem(updatedProblem.id, {
                status: updatedProblem.status
              });
              setProblems(problems.map(p => p.id === updated.id ? updated : p));
              setSelectedProblem(updated);
              toast.success('Status updated');
            } catch (error) {
              toast.error('Failed to update: ' + error.message);
            }
          }}
        />
      )}

      {showFormModal && (
        <ProblemFormModal
          problem={editingProblem}
          onClose={() => {
            setShowFormModal(false);
            setEditingProblem(null);
          }}
          onSubmit={editingProblem ? handleUpdateProblem : handleCreateProblem}
        />
      )}
    </div>
  );
};

export default Dashboard;
